/**
 * Trust Score V2 — frontend mirror of `backend/services/trust_score.py`.
 *
 * Two soft floors prevent fresh tutors from being penalised below half-points
 * in the rating / booking-confirmation categories until they have enough data.
 *
 *   ┌──────────────────────────────────────┬───────┐
 *   │ Factor                               │ Pts   │
 *   ├──────────────────────────────────────┼───────┤
 *   │ Identity & Verification              │  20   │
 *   │ Experience & Education               │  15   │
 *   │ Reviews & Rating Quality             │  30   │
 *   │ Booking-Confirmation Rate            │  20   │ ← NEW (CEO directive)
 *   │ Lesson-Completion Rate               │  10   │ ← NEW
 *   │ Recency / Activity                   │   5   │
 *   │ TOTAL                                │ 100   │
 *   └──────────────────────────────────────┴───────┘
 *
 * Keep this file in sync with the Python module — the two MUST agree on
 * tier thresholds and weights, otherwise the UI will show a different
 * score from what the API returns.
 */

const _scoreIdentity = (t) => {
  let s = 0;
  if (t.is_verified)     s += 15;
  if (t.email_confirmed) s += 3;
  if (t.phone_confirmed) s += 2;
  return Math.min(20, s);
};

const _scoreExperience = (t) => {
  const yrs = Number(t.experience_years || 0);
  let s = Math.min(10, Math.round((yrs / 5) * 10));
  if (t.certifications || t.education) s += 5;
  return Math.min(15, s);
};

const _scoreRating = (t) => {
  const rating  = Number(t.rating || t.average_rating || 0);
  const reviews = Number(t.total_reviews || t.approved_reviews || 0);
  if (rating <= 0) return 0;
  let raw = rating * 6;
  if (reviews < 5) raw *= 0.5;
  return Math.min(30, Math.round(raw));
};

const _scoreConfirmation = (stats) => {
  const c = Number(stats.confirmed_bookings || 0);
  const r = Number(stats.rejected_bookings  || 0);
  const k = Number(stats.completed_bookings || 0);
  const decided = c + r + k;
  if (decided === 0) return 10;        // baseline
  let raw = ((c + k) / decided) * 20;
  if (decided < 3) raw *= 0.5;
  return Math.max(0, Math.min(20, Math.round(raw)));
};

const _scoreCompletion = (stats) => {
  const completed = Number(stats.completed_bookings || 0);
  const confirmed = Number(stats.confirmed_bookings || 0);
  const denom = completed + confirmed;
  if (denom === 0) return 5;
  return Math.max(0, Math.min(10, Math.round((completed / denom) * 10)));
};

const _scoreRecency = (t) => {
  const last = t.last_active_at || t.updated_at;
  if (!last) return 0;
  const dt = new Date(last);
  if (isNaN(dt.getTime())) return 0;
  const days = (Date.now() - dt.getTime()) / 86400000;
  return days <= 30 ? 5 : 0;
};

/** Pick a tier from a 0-100 score (must mirror backend exactly). */
export const tierForScore = (score) =>
  score >= 85 ? 'excellent' :
  score >= 70 ? 'trusted'   :
  score >= 50 ? 'good'      : 'limited';

/**
 * Compute Trust Score V2 with breakdown.
 *
 * @param {object} tutor
 * @param {object} [stats] — optional booking aggregation (the backend's
 *   `/api/tutors/:id/trust-score` returns the full breakdown so callers
 *   should prefer that on profile pages; this helper exists for *list*
 *   pages where a per-card API call would be wasteful).
 * @returns {{score:number, tier:string, breakdown:Array<{key,label,earned,max}>}}
 */
export const computeTrustScoreV2 = (tutor, stats = {}) => {
  if (!tutor) {
    return {
      score: 0, tier: 'limited',
      breakdown: [],
    };
  }
  const parts = [
    ['identity',     'Identity',     _scoreIdentity(tutor),         20],
    ['experience',   'Experience',   _scoreExperience(tutor),       15],
    ['rating',       'Rating',       _scoreRating(tutor),           30],
    ['confirmation', 'Confirmation', _scoreConfirmation(stats),     20],
    ['completion',   'Completion',   _scoreCompletion(stats),       10],
    ['recency',      'Recency',      _scoreRecency(tutor),           5],
  ];
  const earned = parts.reduce((acc, p) => acc + p[2], 0);
  const score  = Math.max(0, Math.min(100, earned));
  return {
    score, tier: tierForScore(score),
    breakdown: parts.map(([key, label, e, m]) => ({ key, label, earned: e, max: m })),
  };
};

/* ────────────────────────────────────────────────────────────────────
 * Backwards-compat shim — keeps `computeTutorTrustScore` working for
 * existing callers (TutorCard etc.) so we don't break anything. Returns
 * just the score integer like V1 did.
 * ──────────────────────────────────────────────────────────────────── */
export const computeTutorTrustScore = (tutor, stats) => computeTrustScoreV2(tutor, stats).score;
